import { useContext } from 'react';
import { Box, Checkbox, FormControlLabel, useMediaQuery, useTheme } from "@mui/material";
import { BulkContext } from '../store/bulklist-filters-context';
import SearchIndicator from './SearchBulk';
import BulkSummary from './BulkRecap';
import { FiltersContext } from '../store/filters-options-context';
import NoData from "./ErrorMessage";
import { fuzzySearchRanked } from '../utils/fuzzySearch';
export default function AllIndicators({ list }) {
    const { searchQuery, checked, setChecked, setSelectedItems } = useContext(BulkContext);
    const { params } = useContext(FiltersContext);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const firstYear = params.year[0];
    const lastYear = params.year.slice(-1).pop();


    const handleToggle = (label, item) => () => {
        const currentIndex = checked.indexOf(label);
        const newChecked = [...checked];

        const itemExists = (selectedItem) =>
            selectedItem.value === item.value

        if (currentIndex === -1) {
            newChecked.push(label);
            setSelectedItems((prevSelectedItems) => [...prevSelectedItems, item]);
        } else {
            newChecked.splice(currentIndex, 1);
            setSelectedItems((prevSelectedItems) =>
                prevSelectedItems.filter((selectedItem) => !itemExists(selectedItem))
            );
        }

        setChecked(newChecked);
    };

    const toggleAllOutcomes = (outcome) => () => {
        // Use filteredList when searching, otherwise use full list
        const outcomes = (searchQuery.trim() ? filteredList : list).filter(item => item.outcome === outcome);
        const outcomeLabels = outcomes.map(item => item.label);
        const selectedOutcomes = outcomes.map(item => ({
            value: item.value,
            outcome: item.outcome,
            theme: item.theme
        }));
        const allChecked = outcomeLabels.every(label => checked.includes(label));
        if (allChecked) {
            setChecked(checked.filter(label => !outcomeLabels.includes(label)));
            setSelectedItems((prevSelectedItems) =>
                prevSelectedItems.filter((selectedItem) =>
                    !selectedOutcomes.some(
                        (outcomeItem) =>
                            outcomeItem.value === selectedItem.value
                    )
                )
            );
        } else {
            setChecked([...checked, ...outcomeLabels.filter(label => !checked.includes(label))]);
            setSelectedItems((prevSelectedItems) => [
                ...prevSelectedItems,
                ...selectedOutcomes.filter(item =>
                    !prevSelectedItems.some(
                        (existingItem) =>
                            existingItem.value === item.value
                    )
                )
            ]);
        }
    };

    const toggleAllThemes = (theme) => () => {
        // Use filteredList when searching, otherwise use full list
        const themeItems = (searchQuery.trim() ? filteredList : list).filter(item => item.theme === theme);
        const themeLabels = themeItems.map(item => item.label);
        const selectedThemes = themeItems.map(item => ({
            value: item.value,
            outcome: item.outcome,
            theme: item.theme
        }));
        const allChecked = themeLabels.every(label => checked.includes(label));

        if (allChecked) {
            setChecked(checked.filter(label => !themeLabels.includes(label)));
            setSelectedItems((prevSelectedItems) =>
                prevSelectedItems.filter((selectedItem) =>
                    !selectedThemes.some(
                        (themeItem) =>
                            themeItem.value === selectedItem.value
                    )
                )
            );
        } else {
            setChecked([...checked, ...themeLabels.filter(label => !checked.includes(label))]);
            setSelectedItems((prevSelectedItems) => [
                ...prevSelectedItems,
                ...selectedThemes.filter(item =>
                    !prevSelectedItems.some(
                        (existingItem) =>
                            existingItem.value === item.value
                    )
                )
            ]);
        }
    };

    const filteredList = searchQuery.trim()
        ? fuzzySearchRanked(searchQuery, list, (item) => `${item.outcome} ${item.theme} ${item.label}`)
        : list;


    const uniqueOutcomes = [...new Set(list.map(item => item.outcome))];

    return (
        <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ fontSize: "12.6px", pb: 8, minHeight: '30vh' }}
        >
            {isMobile &&
                <Box sx={{ position: 'fixed', bottom: '0px', zIndex: '999', width: '100%', pt: 1, backgroundColor: theme.palette.secondary.main, borderTop: (theme) => `1px solid ${theme.palette.secondary.dark}`, left: '0' }}>
                    <SearchIndicator isMobile={isMobile} />
                    <BulkSummary isMobile={isMobile} firstYear={firstYear} lastYear={lastYear} />
                </Box>
            }
            {!filteredList.length > 0 ? (
                <NoData text="Sorry, no indicators matched your search. Please try again" severity='warning' />
            ) : (
                <>
                    {uniqueOutcomes.map((outcome) => {
                        const outcomeItems = filteredList.filter(
                            (item) => item.outcome === outcome
                        );

                        const outcomeLabels = outcomeItems.map((item) => item.label);
                        const isOutcomeChecked = outcomeLabels.every((label) =>
                            checked.includes(label)
                        );
                        const uniqueThemes = [
                            ...new Set(outcomeItems.map((item) => item.theme)),
                        ];

                        return (
                            outcomeItems.length > 0 &&
                            <Box
                                flexBasis={isMobile ? '100%' : '49%'}
                                mb={4}
                                pb={3}
                                key={outcome}
                                sx={{
                                    borderRadius: "15px",
                                    border: (theme) =>
                                        `1px solid ${theme.palette.secondary.dark}`,
                                    backgroundColor: "secondary.light",

                                }}
                            >
                                <Box
                                    key={`outcome-${outcome}`}
                                    sx={{
                                        borderTopLeftRadius: "15px",
                                        borderTopRightRadius: "15px",
                                        pl: isMobile ? 1.5 : 2,
                                        pt: 1,
                                        pb: 1,
                                        pr: isMobile ? 1.5 : 3,
                                        backgroundColor: "secondary.dark",
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Box sx={{ flex: 4, lineHeight: 1.4 }} component='span'><strong>{outcome}</strong></Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    onClick={toggleAllOutcomes(outcome)}
                                                    checked={isOutcomeChecked}
                                                    size="small"
                                                />
                                            }
                                            label={!isMobile ? 'Select all' : ''}
                                            labelPlacement="start"
                                        />
                                    </Box>
                                </Box>
                                {uniqueThemes.map((themes) => {
                                    const themeItems = outcomeItems.filter(
                                        (item) => item.theme === themes
                                    );
                                    const themeLabels = themeItems.map((item) => item.label);
                                    const isThemeChecked = themeLabels.every((label) =>
                                        checked.includes(label)
                                    );

                                    return (
                                        <Box
                                            key={`theme-${themes}`}
                                            sx={{ pt: 2, pb: 0, pr: isMobile ? 1.5 : 3, pl: isMobile ? 1.5 : 3 }}
                                        >
                                            {/* Only show theme header if theme name is not empty */}
                                            {themes && themes.trim() !== '' && (
                                                <Box
                                                    sx={{
                                                        borderBottom: (theme) =>
                                                            `1px solid ${theme.palette.primary.fontColor}30`,
                                                        mb: 1,
                                                        fontWeight: "600",
                                                        fontSize: "13px",
                                                    }}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                >
                                                    <Box component='span' sx={{ flex: 4, lineHeight: 1.4, pb: '5px' }}>{themes}</Box>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                onClick={toggleAllThemes(themes)}
                                                                checked={isThemeChecked}
                                                                size="small"
                                                            />
                                                        }
                                                        label={!isMobile ? 'Select all' : ''}
                                                        labelPlacement="start"
                                                    />
                                                </Box>
                                            )}
                                            {themeItems.map((item) => (
                                                <Box key={item.id + item.label} sx={{ pl: 2 }}>
                                                    <FormControlLabel
                                                        sx={{ alignItems: 'flex-start' }}
                                                        control={
                                                            <Checkbox
                                                                sx={{ p: 0.5, pl: 1, paddingTop: "1px" }}
                                                                edge="start"
                                                                size="small"
                                                                checked={checked.includes(item.label)}
                                                                onClick={handleToggle(item.label, item)}
                                                                tabIndex={-1}
                                                                disableRipple
                                                                inputProps={{
                                                                    "aria-labelledby": `checkbox-list-label-${item.label}`,
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <span style={{ fontSize: "12px" }}>
                                                                {item.label}
                                                            </span>
                                                        }
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </>
            )}
        </Box>
    );
}
